import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CMS_DEFAULT_SCREEN,
  CMS_SCREENS_BY_KEY,
  CMS_SCREENS_BY_ROUTE_KEY,
  cmsRouteKey,
  type CmsScreen,
} from './cmsScreenRegistry';

/**
 * The CMS Admin hash contract — **MEASURED against the running page on
 * 2026-08-05**, not read off `controller/router.js`.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ★★ DO NOT COPY THE USER-SETTINGS PATTERN HERE.
 *
 * User Settings had a DEAD `controller/router.js`: `Backbone.history.start()`
 * ran with zero routes, the fragment was read ONCE at boot, and Back/Forward
 * moved the URL without moving the view.
 *
 * CMS Admin is the OPPOSITE. `app.js:1390` starts history with a real route
 * table, so a `hashchange` DOES fire `changeAppState` → `open:<tab>` → the
 * screen opens. Measured: hash `#manufacturer` → Back → hash `#document` and
 * `#document` is the visible screen.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * The four measured rules:
 *
 * 1. **Boot** lands on `#manufacturer` — `router.js:88 start()` fires
 *    `open:manufacturer`, whose handler ends in `navigate("manufacturer")`.
 *    ★ That happens ONLY for an EMPTY fragment. An UNKNOWN fragment is a
 *    different case and was re-measured on 2026-08-05 with a hard reload:
 *      `/admin/#nonsense-not-a-tab` → hash STAYS `#nonsense-not-a-tab`, and
 *      NO screen is shown at all (`#menuTab` has no visible child).
 *      `/admin/#roof_curb`          → same: hash kept, blank content.
 *    Backbone matches no route, so nothing opens and nothing rewrites the hash.
 *    Same for a live hashchange to an unknown fragment: the CURRENT screen
 *    stays put (measured: on `#document`, set `#nonsense` → `document` is still
 *    the visible screen). Falling back to the default screen here would both
 *    rewrite the user's URL and mask a tranche's broken deep link.
 *
 * 2. **The hash is WRITTEN by the screen-open handler, never by the click.**
 *    `api.js` ends every `open_*` with `Backbone.history.navigate(<hash>)`;
 *    product screens get theirs from `product.menu.js:open_tabs`. Shapes:
 *      admin   → `#<tabkey>`
 *      product → `#products/<productId>/<parent>/<tabkey>`
 *      base    → `#products/<id>/construction/base/<sub>/<last>` (the ONLY
 *                screen with two extra segments; they come from localStorage)
 *
 * 3. **`navigate()` is called WITHOUT `{trigger: true}`**, so writing the hash
 *    does not re-fire the route. `replace` is not used either, so every tab
 *    open pushes a history entry — which is what makes rule 4 observable.
 *    Here: `openScreen` writes the hash and sets state in one go, and the
 *    `hashchange` listener no-ops when the hash already matches the state.
 *
 * 4. **Back/Forward move the view.** Handled by the `hashchange` listener.
 *
 * Not modelled: `router.js:clickToElement`'s 200–1000 ms `setTimeout` +
 * synthesised `.click()`. That exists only because legacy had no way to open a
 * screen except through the DOM; it is an implementation detail, and its
 * observable consequence (a hash pointing at a screen with no nav anchor is a
 * silent no-op that leaves the content area BLANK) is preserved here by
 * resolving such a hash to the empty screen key `''`.
 */

/** No screen open — legacy's state after an unmatched fragment. */
const CMS_NO_SCREEN = '';

export interface CmsRoute {
  /** The tab key currently shown. Always a reachable key. */
  screenKey: string;
  /** Product id from the hash, when the hash is a product route. */
  productId?: string;
  /**
   * ★ T7 (base only) — the TWO extra hash segments
   * `#products/<id>/construction/base/<sub>/<last>` (router.js:19-21,58-62). The
   * ONLY screen whose hash carries them; they drive base's outer (`tubes`/`cch`)
   * and inner (`perimeter`/`primary`/`secondary`) tab. Legacy's `base.js`/`tubes.js`
   * read them out of `localStorage` (`activeTab`/`activeSubTab`), which
   * `router.js:openProductTabs` writes from THESE segments; the React `base`
   * screen reads them straight off this route (and mirrors the localStorage
   * writes for persistence + product.menu-style nav-open). `undefined` for every
   * other screen — the deferral note above is now discharged for base only.
   */
  sub?: string;
  last?: string;
  /**
   * Set when legacy REWRITES the address bar after matching this hash, so the
   * URL the user ends up with is not the one they typed.
   *
   * ★ T2, measured on `:8130`: `#manufacturer/anything` matches the real route
   * `'manufacturer/:tab'` (router.js:14), whose handler **ignores `:tab`** and
   * calls `changeAppState('manufacturer')` — which ends in
   * `Backbone.history.navigate("manufacturer")`. Loading
   * `/admin/#manufacturer/foo` therefore leaves the address bar reading
   * `#manufacturer`. The un-repaired shell showed the right screen but kept
   * `#manufacturer/foo`.
   *
   * ⚠ Deliberately NOT generalised to "always rewrite to the canonical hash".
   * The Base screen's hash carries two extra segments
   * (`products/<id>/construction/base/<sub>/<last>`, survey §3.2) that
   * `buildCmsHash` cannot reconstruct, so a blanket canonicalisation would
   * silently truncate them when T7 lands. Only routes actually measured to
   * rewrite set this.
   */
  normalizeTo?: string;
}

/**
 * Resolve one hash segment to a screen.
 *
 * ★ T2: keyed on the HASH FRAGMENT (`routeKey`), not the DOM slot id. They are
 * the same string for every screen but `all_products`, whose hash is
 * `#products` — resolving that by slot id found nothing and left the content
 * area blank where legacy shows the screen.
 *
 * ⚠ ROUTE-KEY ONLY, NO `BY_KEY` FALLBACK. The fallback shipped in the first cut
 * of this change and was caught by T2's independent verifier: because
 * `routeKey` defaults to `key`, the ONLY extra string it accepted was
 * `all_products` itself — and legacy does NOT accept it. Measured side by side
 * from `#document`, setting `#all_products`:
 *     :8130  hash kept, `document` still the visible screen (the dispatcher has
 *            no `open:all_products` entry — dispatcher.js has `open:products`
 *            only — and `clickToElement` finds no `a[value="all_products"]`)
 *     :8005  (with the fallback) OPENED the products board — 384 chars
 * i.e. the fallback invented a route legacy does not have, contradicting rule 1
 * above ("an UNKNOWN fragment opens nothing"). Every screen is still reachable
 * by its own key, because `cmsRouteKey` defaults to it and the registry has no
 * duplicate key OR routeKey (checked across all 74 rows).
 */
const screenForHashSegment = (segment: string): CmsScreen | undefined =>
  CMS_SCREENS_BY_ROUTE_KEY[segment];

/**
 * The parent segments the legacy router ENUMERATES (router.js:18-24). A product
 * hash whose parent is not one of these matches NO route — T6 shell-pass,
 * measured on `:8130`: from `#document`, set `#products/1/bogusparent/settCost`
 * → hash kept, `document` still the visible screen. The pre-T6 parser accepted
 * ANY parent (it only read `parts[3]`) and wrongly opened `settCost`.
 */
const CMS_PRODUCT_HASH_PARENTS = new Set([
  'settings',
  'construction',
  'modular_frame',
  'components',
  'accessories',
]);

/** Parse a location hash into a route. Returns `null` if it names nothing usable.
 *
 * `bootProductId` is the menu's hard-coded product (`main.menu.js:44`), needed
 * to build the canonical hash legacy rewrites to (see `normalizeTo` below). */
export const parseCmsHash = (raw: string, bootProductId: number | string = 1): CmsRoute | null => {
  const hash = raw.replace(/^#/, '');
  if (!hash) return null;

  /**
   * ★ A TRAILING SLASH MATCHES NOTHING. Backbone's `routeStripper`
   * (`/^[#\/]|\s+$/g`) strips a LEADING `#` or `/` and trailing WHITESPACE —
   * never a trailing slash — and every route regex is `^…$`, so `freight/`
   * fails `^([^/?]+)$`. The `.filter(Boolean)` below would otherwise swallow the
   * empty last segment and make `#freight/` a synonym for `#freight`.
   * Measured from `#document`: `:8130` set `#freight/` → hash kept, `document`
   * still visible; `:8005` (before this guard) opened `freight`. Found by T2's
   * independent verifier. Leading slashes are still fine — `#/freight` IS
   * `freight` on Backbone, and `.filter(Boolean)` keeps that working.
   */
  if (hash.endsWith('/')) return null;

  // product route: products/<id>/<parent>/<tab>[/<sub>/<last>]
  const parts = hash.split('/').filter(Boolean);
  if (parts[0] === 'products' && parts.length >= 3) {
    const productId = parts[1];

    /**
     * ★ T6 SHELL-PASS (2026-08-10): this arm used to accept ANY parent segment,
     * any segment count ≥3, and any tail — looser than the legacy route table
     * on all three axes. Five divergences measured side-by-side from a parked
     * `#document` (`:8130` = truth; every case below names the LEGACY end state):
     *   #products/1/bogusparent/settCost   → stays put (parent not enumerated)
     *   #products/1/settings/settCost/x    → stays put (settings route is
     *                                        exactly 4 segments; `:tabName`
     *                                        never matches a `/`)
     *   #products/1/settCost               → stays put (the only 3-segment
     *                                        routes are the LITERAL
     *                                        `special_options` and `cnv`,
     *                                        router.js:25-26)
     *   #products/1/settings/mf_parts      → OPENS mf_parts and REWRITES the
     *                                        hash to #products/1/modular_frame/
     *                                        mf_parts (`open_tabs` ends in
     *                                        `navigate(<canonical>)`)
     *   #products/999/settings/settOptions → OPENS and REWRITES to id 1 (the
     *                                        menu's product, main.menu.js:44)
     * Bonus, same mechanism: #products/1/settings/freight → legacy opens the
     * ADMIN freight screen and rewrites to bare `#freight` (measured). The
     * rewrite is `buildCmsHash` of the RESOLVED screen — its own section,
     * parent and the boot product id — so all three rewrite cases fall out of
     * one rule.
     */

    // 3-segment: the literal no-parent routes. `cnv` is the only live one --
    // `special_options` was the other until the page moved into Construction
    // ▸ Class, and is redirected there so bookmarks and old links still land
    // somewhere useful instead of on a blank panel.
    if (parts.length === 3) {
      if (parts[2] === 'special_options') {
        const moved = screenForHashSegment('cnstrSettings');
        if (moved)
          return {
            screenKey: moved.key,
            productId,
            normalizeTo: buildCmsHash(moved, bootProductId),
          };
        return null;
      }
      const screen = screenForHashSegment(parts[2]);
      if (screen && screen.status !== 'dead' && screen.section === 'product' && !screen.hashParent)
        return { screenKey: screen.key, productId };
      return null;
    }

    // 4+ segments: the parent must be one legacy enumerates…
    const parent = parts[2];
    if (!CMS_PRODUCT_HASH_PARENTS.has(parent)) return null;
    // …and only construction routes carry extra segments (4–6; router.js:19-21).
    if (parent !== 'construction' && parts.length !== 4) return null;
    if (parts.length > 6) return null;

    const screen = screenForHashSegment(parts[3]);
    if (!screen || screen.status === 'dead') return null;

    /**
     * Canonicalisation, EXACT-4 NON-CONSTRUCTION ROUTES ONLY. `construction`
     * is deliberately excluded: `base`'s hash carries two extra localStorage
     * segments `buildCmsHash` cannot reconstruct (the standing T2 warning
     * above), and construction screens are all T7 — that tranche owns their
     * hash semantics. The one remaining 3-segment literal, `cnv`, keeps its
     * typed hash too: legacy's `#products/<odd-id>/cnv` path detours through
     * `open:product` with that id and was NOT measured; parked rather than
     * guessed. (`special_options` was the other such literal until the page
     * moved into Construction ▸ Class; its hash now normalizes to the class
     * screen — see the 3-segment branch above.)
     */
    if (parent !== 'construction') {
      const canonical = buildCmsHash(screen, bootProductId);
      if (canonical !== hash) return { screenKey: screen.key, productId, normalizeTo: canonical };
    }
    // ★ T7 (base): carry the two extra segments so the base screen can restore
    // its outer/inner tab from a cold deep-link. `parts[4]`/`parts[5]` may be
    // absent (bare `…/base`) or PREFIXED (`…/base/tubes/tubes_perimeter`, the
    // nav-open shape product.menu.js:205 writes); base normalises both. Only
    // `base` gets them — every other construction screen is exactly 4 segments.
    if (screen.key === 'base') {
      return { screenKey: screen.key, productId, sub: parts[4], last: parts[5] };
    }
    return { screenKey: screen.key, productId };
  }

  /**
   * `'manufacturer/:tab': 'openManufacturer'` (router.js:14) — a REAL route, and
   * its handler ignores the `:tab` and just does `changeAppState('manufacturer')`.
   * So any two-segment hash under `manufacturer` opens the manufacturer screen.
   */
  if (parts[0] === 'manufacturer' && parts.length === 2) {
    const mfg = CMS_SCREENS_BY_KEY.manufacturer;
    // The handler ends in `navigate("manufacturer")`, so the URL is rewritten.
    if (mfg && mfg.status !== 'dead') return { screenKey: mfg.key, normalizeTo: mfg.key };
    return null;
  }

  /**
   * ★ The admin route is Backbone's `':tab'` (router.js:13), and a Backbone
   * `:param` NEVER matches a `/`. So a multi-segment hash that is not one of the
   * product routes above matches NO route — legacy leaves the view where it is.
   * ⚠ `'products/:id'` is COMMENTED OUT (router.js:17), so `#products/1` — which
   * a product-tile click actually writes — is exactly this case. Matching it on
   * `parts[0]` would wrongly move the view to `all_products` on a Back/Forward.
   */
  if (parts.length !== 1) return null;

  const screen = screenForHashSegment(parts[0]);
  if (screen && screen.status !== 'dead') return { screenKey: screen.key };
  return null;
};

/** Build the hash a screen writes when it opens (rule 2).
 *
 * ★ T7 ACTIVATED the `extra` arm: only `base` passes it, so its nav-open hash is
 * `products/<id>/construction/base/<sub>/<last>` exactly as product.menu.js:266-268
 * builds it. Every other screen passes no `extra` and is unaffected. */
export const buildCmsHash = (
  screen: CmsScreen,
  productId: number | string,
  extra?: { sub?: string; last?: string }
): string => {
  // ★ T2: the hash carries `routeKey`, which is `key` for every screen except
  // `all_products` (slot `all_products`, hash `#products`).
  if (screen.section === 'admin') return cmsRouteKey(screen);
  const parent = screen.hashParent ? `${screen.hashParent}/` : '';
  let path = `products/${productId}/${parent}${cmsRouteKey(screen)}`;
  if (extra?.sub && extra?.last) path += `/${extra.sub}/${extra.last}`;
  return path;
};

/**
 * ★ T7 (base only) — the `{sub,last}` product.menu.js:203-205 appends to base's
 * nav-open hash, read straight from `localStorage` (the same keys base itself
 * writes on every tab change): `activeTab||'tubes'` and `activeSubTab||'perimeter'`.
 * `activeSubTab` is stored PREFIXED (`tubes_primary`) and product.menu.js uses it
 * RAW as `last`, so the nav-open hash is `…/base/tubes/tubes_primary`; the base
 * screen normalises the prefix on read. Returns `undefined` (⇒ no extra segments)
 * for every other key, so `openScreen` is byte-identical for the other screens. */
export const baseHashExtra = (key: string): { sub: string; last: string } | undefined => {
  if (key !== 'base') return undefined;
  return {
    sub: localStorage.getItem('activeTab') || 'tubes',
    last: localStorage.getItem('activeSubTab') || 'perimeter',
  };
};

export interface UseCmsHashRoute {
  screenKey: string;
  /** Open a screen: writes the hash (push, no trigger) and moves the view. */
  openScreen: (key: string) => void;
}

export function useCmsHashRoute(productId: number | string): UseCmsHashRoute {
  const [screenKey, setScreenKey] = useState<string>(() => {
    const parsed = parseCmsHash(window.location.hash, productId);
    if (parsed) return parsed.screenKey;
    // Empty fragment → the boot default. Unknown fragment → nothing opens.
    return window.location.hash.replace(/^#/, '') ? CMS_NO_SCREEN : CMS_DEFAULT_SCREEN;
  });
  // Hash values this hook wrote itself, so the listener can tell a Back/Forward
  // (must move the view) from our own write (must not re-enter).
  const selfWritten = useRef<string | null>(null);

  const openScreen = useCallback(
    (key: string) => {
      const screen = CMS_SCREENS_BY_KEY[key];
      if (!screen || screen.status === 'dead') return;
      // ★ T7: `baseHashExtra` is `undefined` for all keys but `base`, so this is
      // the same call as before for the other 33 screens.
      const hash = buildCmsHash(screen, productId, baseHashExtra(key));
      selfWritten.current = hash;
      // push, not replace, and without re-dispatching — matches
      // `Backbone.history.navigate(path)` with no options.
      if (window.location.hash.replace(/^#/, '') !== hash) {
        window.history.pushState(null, '', `#${hash}`);
      }
      setScreenKey(key);
    },
    [productId]
  );

  // Rule 1: an EMPTY boot fragment opens the default screen AND writes the URL,
  // exactly as `router.js:start()` does by firing `open:manufacturer` (whose
  // handler then navigates). An unknown fragment is left strictly alone —
  // legacy neither opens a screen nor rewrites the hash.
  useEffect(() => {
    const raw = window.location.hash.replace(/^#/, '');
    if (!raw) {
      openScreen(CMS_DEFAULT_SCREEN);
      return;
    }
    // ★ T2: a route legacy REWRITES (see `normalizeTo`). Measured on `:8130`,
    // a cold `/admin/#manufacturer/foo` ends with the address bar reading
    // `#manufacturer`, because the route handler ignores `:tab` and its
    // `open:` handler calls `navigate("manufacturer")`. T6 added the product
    // rewrites (wrong/mismatched parent, odd id) — same flow.
    const parsed = parseCmsHash(window.location.hash, productId);
    if (parsed?.normalizeTo && raw !== parsed.normalizeTo) openScreen(parsed.screenKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Rule 4: Back/Forward move the view.
  useEffect(() => {
    const onHashChange = () => {
      const current = window.location.hash.replace(/^#/, '');
      if (selfWritten.current === current) {
        selfWritten.current = null;
        return;
      }
      const parsed = parseCmsHash(window.location.hash, productId);
      // Unmatched fragment = Backbone matched no route = nothing happened.
      // The view must NOT move (measured), so leave `screenKey` untouched.
      if (!parsed) return;
      // A rewriting route also rewrites on a live hashchange, not just at boot.
      if (parsed.normalizeTo && current !== parsed.normalizeTo) {
        openScreen(parsed.screenKey);
        return;
      }
      setScreenKey(parsed.screenKey);
    };
    window.addEventListener('hashchange', onHashChange);
    window.addEventListener('popstate', onHashChange);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('popstate', onHashChange);
    };
    // `openScreen` is stable for the life of the page (`productId` is the
    // constant boot product), so the listener is registered once — re-binding it
    // on every render would drop and re-add a global listener for no gain.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { screenKey, openScreen };
}
